import { larkAPI } from './lark-api-worker.js';
import { cashFlowEngine } from './cashflow-engine.js';
import { cashFlowUI } from './ui.js';
import { LARK_CONFIG } from './config.js';
import './styles.css';

/**
 * Main Application Controller
 */
class CashFlowApp {
  constructor() {
    this.isInitialized = false;
    this.currentFiscalYear = null;
  }

  /**
   * Initialize the application
   */
  async init() {
    try {
      console.log('Initializing Cashflow Plugin...');
      console.log('Environment:', {
        hostname: window.location.hostname,
        pathname: window.location.pathname,
        userAgent: navigator.userAgent
      });
      
      // Initialize UI
      console.log('Step 1: Initializing UI...');
      cashFlowUI.init('app');
      cashFlowUI.showLoading();
      
      // Set up event handlers
      console.log('Step 2: Setting up event handlers...');
      cashFlowUI.onFiscalYearChange = (year) => this.handleFiscalYearChange(year);
      cashFlowUI.onRefresh = () => this.refresh();
      
      // Load initial data
      console.log('Step 3: Loading data...');
      await this.loadData();
      
      this.isInitialized = true;
      console.log('✅ Cashflow Plugin initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize app:', error);
      console.error('Error stack:', error.stack);
      cashFlowUI.showError(`初期化エラー: ${error.message}\n\nブラウザのコンソールを確認してください。`);
    }
  }

  /**
   * Load transaction data from Lark BASE
   */
  async loadData() {
    try {
      console.log('📊 Loading transaction data...');
      console.log('Using table ID:', LARK_CONFIG.TRANSACTIONS_TABLE_ID);
      
      // Get all transactions
      console.log('Calling API...');
      const records = await larkAPI.getAllRecords(LARK_CONFIG.TRANSACTIONS_TABLE_ID);
      console.log(`✅ Loaded ${records.length} transaction records`);
      
      if (records.length === 0) {
        console.warn('No transaction records found');
        cashFlowUI.showError('取引データが見つかりません。Lark BASEにデータを追加してください。');
        return;
      }
      
      // Parse transactions with error handling
      const transactions = records
        .map(record => {
          try {
            return larkAPI.parseTransaction(record);
          } catch (error) {
            console.error('Failed to parse record:', record, error);
            return null;
          }
        })
        .filter(t => t !== null && t.transaction_date !== null);
      
      console.log(`Successfully parsed ${transactions.length} transactions`);
      
      if (transactions.length === 0) {
        cashFlowUI.showError('有効な取引データがありません。取引日フィールドを確認してください。');
        return;
      }
      
      // Load into cashflow engine
      cashFlowEngine.loadTransactions(transactions);
      
      // Get available fiscal years
      const fiscalYears = cashFlowEngine.getAvailableFiscalYears();
      const currentFiscalYear = cashFlowEngine.getCurrentFiscalYear();
      
      console.log('Available fiscal years:', fiscalYears);
      console.log('Current fiscal year:', currentFiscalYear);
      
      if (fiscalYears.length === 0) {
        cashFlowUI.showError('会計年度を特定できませんでした。取引日を確認してください。');
        return;
      }
      
      // Update UI with fiscal year options
      cashFlowUI.updateFiscalYearOptions(fiscalYears, currentFiscalYear);
      
      // Display data for current fiscal year
      await this.displayFiscalYear(currentFiscalYear);
      
    } catch (error) {
      console.error('Failed to load data:', error);
      throw error;
    }
  }

  /**
   * Display cashflow data for specific fiscal year
   * @param {number} fiscalYear - Fiscal year to display
   */
  async displayFiscalYear(fiscalYear) {
    try {
      console.log(`Displaying data for fiscal year ${fiscalYear}`);
      
      this.currentFiscalYear = fiscalYear;
      
      // Get year summary
      const yearSummary = cashFlowEngine.getCashFlowByFiscalYear(fiscalYear);
      console.log('Year summary:', yearSummary);
      
      // Get monthly breakdown
      const monthlySummaries = cashFlowEngine.getMonthlyBreakdown(fiscalYear);
      console.log('Monthly summaries:', monthlySummaries);
      
      // Update UI components
      cashFlowUI.updateSummaryCards(yearSummary);
      cashFlowUI.updateMonthlyTrendChart(monthlySummaries);
      cashFlowUI.updateCategoryBreakdownChart(yearSummary);
      cashFlowUI.updateDetailedBreakdown(monthlySummaries);
      
    } catch (error) {
      console.error('Failed to display fiscal year data:', error);
      cashFlowUI.showError(error.message);
    }
  }

  /**
   * Handle fiscal year change
   * @param {number} year - Selected fiscal year
   */
  async handleFiscalYearChange(year) {
    if (year === this.currentFiscalYear) return;
    
    console.log(`Fiscal year changed to: ${year}`);
    await this.displayFiscalYear(year);
  }

  /**
   * Refresh data from Lark BASE
   */
  async refresh() {
    console.log('Refreshing data...');
    cashFlowUI.showLoading();
    
    try {
      await this.loadData();
    } catch (error) {
      console.error('Failed to refresh data:', error);
      cashFlowUI.showError('データの更新に失敗しました: ' + error.message);
    }
  }
}

// Create and initialize app when DOM is ready
let app;

// Wait for Chart.js to be ready
function waitForChartJS() {
  return new Promise((resolve, reject) => {
    console.log('🔍 Starting Chart.js check...');
    console.log('Current Chart status:', typeof Chart);
    
    // If already loaded, resolve immediately
    if (typeof Chart !== 'undefined') {
      console.log('✅ Chart.js already loaded!');
      resolve();
      return;
    }
    
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds maximum wait
    
    const checkChart = setInterval(() => {
      attempts++;
      
      if (attempts % 10 === 0) {
        console.log(`Still checking for Chart.js... (${attempts}/${maxAttempts})`);
      }
      
      if (typeof Chart !== 'undefined') {
        console.log(`✅ Chart.js loaded after ${attempts} attempts!`);
        console.log('Chart.js version:', Chart.version);
        clearInterval(checkChart);
        resolve();
      } else if (attempts >= maxAttempts) {
        console.error('❌ Chart.js failed to load after 10 seconds');
        clearInterval(checkChart);
        reject(new Error('Chart.jsの読み込みがタイムアウトしました。ネットワーク接続を確認してください。'));
      }
    }, 100);
  });
}

async function initApp() {
  try {
    console.log('🚀 Starting CashFlow App initialization...');
    
    // Wait for Chart.js to load
    console.log('⏳ Waiting for Chart.js to load...');
    await waitForChartJS();
    
    console.log('✅ Chart.js loaded, initializing app...');
    app = new CashFlowApp();
    await app.init();
    
  } catch (error) {
    console.error('❌ App initialization failed:', error);
    // Display user-friendly error
    const appDiv = document.getElementById('app');
    if (appDiv) {
      appDiv.innerHTML = `
        <div style="padding: 40px; text-align: center; font-family: sans-serif;">
          <h2 style="color: #d32f2f;">❌ アプリケーションの起動に失敗しました</h2>
          <p style="margin: 20px 0; color: #666;">
            エラー: ${error.message}
          </p>
          <p style="color: #999; font-size: 14px;">
            ブラウザのコンソールを開いて詳細を確認してください（F12キー）
          </p>
          <button onclick="location.reload()" style="
            margin-top: 20px;
            padding: 10px 20px;
            background: #2196F3;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
          ">
            🔄 再読み込み
          </button>
        </div>
      `;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}

// Export for external access
export { app };
